import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import PoojaCard from './PoojaCard.jsx'

const PER_PAGE = 8
const SEARCH_DEBOUNCE_MS = 400

function SkeletonCard() {
  return (
    <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden animate-pulse flex flex-col h-full">
      <div className="aspect-4/3 bg-gray-200" />
      <div className="p-6 space-y-4 flex-1">
        <div className="h-3 w-32 rounded bg-gray-100" />
        <div className="h-5 w-full rounded bg-gray-200" />
        <div className="h-5 w-2/3 rounded bg-gray-200" />
        <div className="h-3 w-40 rounded bg-gray-100" />
        <div className="pt-4 border-t border-dashed border-gray-100 flex justify-between items-center mt-auto">
          <div className="h-6 w-20 rounded bg-gray-200" />
          <div className="h-10 w-28 rounded-xl bg-gray-100" />
        </div>
      </div>
    </div>
  )
}

/** The bookable-puja storefront: search + category tabs + paginated grid,
 * server-side filtering via GET /v1/pujas (same load-more idiom as the
 * Products page). */
export default function PoojaListingSection() {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('ALL')
  const [page, setPage] = useState(1)

  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  // Accumulated rows across all loaded pages
  const [loadedPoojas, setLoadedPoojas] = useState([])
  const mergedPagesRef = useRef(new Set())

  const { data: catData, isLoading: catsLoading } = useQuery({
    queryKey: ['pujaCategories'],
    queryFn: () => api.puja.categories(),
    staleTime: 10 * 60 * 1000,
  })
  const categories = catData?.data ?? []
  const categoryTabs = useMemo(
    () => [{ id: null, slug: 'ALL', title: 'ALL POOJAS', icon: null }, ...categories],
    [categories],
  )
  const activeCat = category !== 'ALL' ? categories.find((c) => c.slug === category) : null

  const q = debouncedQuery.trim()
  useEffect(() => {
    setPage(1)
    setLoadedPoojas([])
    mergedPagesRef.current = new Set()
  }, [q, category])

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['pujaList', q, category, page],
    queryFn: () =>
      api.puja.list({
        ...(q && { search: q }),
        ...(activeCat && { category: activeCat.id }),
        page,
        limit: PER_PAGE,
      }),
    staleTime: 2 * 60 * 1000,
  })

  useEffect(() => {
    if (!pageData || mergedPagesRef.current.has(page)) return
    const incoming = pageData?.data ?? []
    if (incoming.length === 0) return
    mergedPagesRef.current.add(page)
    setLoadedPoojas((prev) => {
      const ids = new Set(prev.map((p) => p.id))
      return [...prev, ...incoming.filter((p) => !ids.has(p.id))]
    })
  }, [pageData, page])

  const serverTotal = pageData?.meta?.total ?? 0
  const lastPage = pageData?.meta?.last_page ?? 1
  const canLoadMore = page < lastPage
  const isFirstLoad = isFetching && loadedPoojas.length === 0

  const resetFilters = () => {
    setQuery('')
    setCategory('ALL')
  }

  return (
    <section id="book-pooja" className="py-14 md:py-20 bg-white md:px-20 scroll-mt-24">
      <div className="container px-6 mx-auto max-w-350">
        {/* Heading */}
        <div className="max-w-2xl mx-auto text-center space-y-3 mb-10">
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-secondary/50"></span>
            <span className="text-[10px] uppercase tracking-[0.4em] text-secondary font-bold">Book A Sacred Ritual</span>
            <span className="h-px w-8 bg-secondary/50"></span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase leading-tight text-primary">
            Upcoming Temple <span className="text-gradient-gold font-bold block">Poojas &amp; Daan Seva</span>
          </h2>
          <p className="text-xs md:text-sm leading-relaxed text-gray-500 normal-case">
            Authentic Vedic rituals performed by verified temple priests in your name and gotra —
            with sankalp video, live updates and prasad at your doorstep.
          </p>
        </div>

        {/* FILTER CONSOLE */}
        <div className="bg-white rounded-2xl border border-gray-100 px-5 py-4 mb-8 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-lg">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-primary/30 text-xs"></i>
              <input
                id="pooja-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="SEARCH POOJA, TEMPLE OR DEITY..."
                className="w-full h-10 bg-gray-50/50 border border-gray-100 rounded-xl pl-10 pr-9 focus:ring-2 focus:ring-secondary/20 focus:border-secondary/40 outline-none text-[10px] font-mono tracking-wider transition-all"
                type="text"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/30 hover:text-secondary transition-colors"
                >
                  <i className="fa-solid fa-xmark text-xs"></i>
                </button>
              )}
            </div>

            {(query || category !== 'ALL') && (
              <button
                type="button"
                onClick={resetFilters}
                className="h-9 px-4 rounded-xl text-[9px] font-bold tracking-widest border border-secondary/40 text-secondary hover:bg-secondary hover:text-white transition-all flex items-center gap-2 self-start lg:self-auto"
              >
                <i className="fa-solid fa-filter-circle-xmark"></i> CLEAR
              </button>
            )}
          </div>

          {/* Category tabs */}
          <div className="pt-3 border-t border-dashed border-gray-100">
            <div className="flex items-center gap-3 overflow-x-auto pb-1 scrollbar-none">
              {catsLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-9 w-28 rounded-xl bg-gray-100 animate-pulse shrink-0" />
                ))
                : categoryTabs.map((c) => {
                  const active = c.slug === category || (c.slug === 'ALL' && category === 'ALL')
                  return (
                    <button
                      key={c.slug}
                      id={`pooja-tab-${c.slug}`}
                      type="button"
                      onClick={() => setCategory(c.slug)}
                      className={`h-9 px-5 rounded-xl text-[9px] font-bold tracking-widest transition-all whitespace-nowrap border flex items-center gap-2 uppercase ${
                        active
                          ? 'bg-primary text-white border-primary shadow-md shadow-primary/10'
                          : 'bg-white border-gray-200 text-primary/80 hover:border-secondary hover:text-secondary hover:bg-background/30'
                      }`}
                    >
                      {c.icon
                        ? <img alt={c.title} className="w-5 h-5 rounded-full object-cover shrink-0" src={c.icon} />
                        : <div className="h-1 w-1 rounded-full bg-secondary animate-pulse"></div>
                      }
                      {c.title}
                      {active && (
                        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-md ml-1 text-white/50 bg-black/10">
                          {isFetching ? '…' : serverTotal}
                        </span>
                      )}
                    </button>
                  )
                })
              }
            </div>
          </div>
        </div>

        {/* GRID */}
        {isFirstLoad ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {Array.from({ length: PER_PAGE }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : loadedPoojas.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {loadedPoojas.map((p) => <PoojaCard key={p.id} pooja={p} />)}
          </div>
        ) : (
          <div className="text-center py-24 normal-case">
            <i className="fa-solid fa-hands-praying text-3xl text-primary/20"></i>
            <p className="mt-4 text-sm text-primary/50 font-body">
              No poojas match your current filters.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary/5 border border-primary/10 text-[10px] font-bold text-primary/60 tracking-widest hover:bg-primary hover:text-white transition-all uppercase"
            >
              <i className="fa-solid fa-rotate-left"></i> RESET FILTERS
            </button>
          </div>
        )}

        {/* LOAD MORE / END STATE */}
        {!isFirstLoad && loadedPoojas.length > 0 && (
          <div className="mt-14 flex flex-col items-center justify-center gap-4">
            {canLoadMore ? (
              <button
                type="button"
                onClick={() => !isFetching && setPage((v) => v + 1)}
                disabled={isFetching}
                className="inline-flex items-center justify-center gap-3 px-8 h-12 rounded-xl bg-primary text-white font-semibold text-[10px] tracking-[0.2em] uppercase transition-all duration-300 hover:bg-secondary shadow-lg shadow-primary/10 active:scale-95 border-none outline-none cursor-pointer disabled:opacity-70"
              >
                {isFetching && <i className="fa-solid fa-spinner text-sm animate-spin"></i>}
                {isFetching ? 'Loading...' : 'Load More Poojas'}
              </button>
            ) : (
              <p className="text-[10px] font-mono tracking-[0.25em] text-secondary font-bold uppercase bg-secondary/5 px-6 py-2.5 rounded-full border border-secondary/10">
                ✨ All {serverTotal} Poojas Loaded
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
